import { HTTPError } from 'ky';
import i18next from 'i18next';
import { http } from '@/lib/http';
import { asLockConflict } from '@/lib/locks/api';
import type { CaseCareCard, IDisruptionTotalSpend } from './types';
import {
  BulkInProgressError,
  SyncRunInProgressError,
  TransportSeamError,
  type AddRunInput,
  type AddRunResult,
  type AirportOption,
  type BulkAllocateResult,
  type BulkAutoRebookResult,
  type BulkInProgress409,
  type CareBoard,
  type CareCardHistory,
  type CareLoads,
  type CareMoney,
  type CareLookupResult,
  type CareOutcomeKind,
  type CareRedeemResult,
  type CareTopUpInput,
  type CareTopUpResult,
  type ApplyBenefitOverrideResult,
  type BenefitOverride,
  type ProposeBenefitOverrideInput,
  type DisruptionPriceBreakdown,
  type DisruptionDetail,
  type DisruptionFilters,
  type DisruptionListRow,
  type DisruptionOverview,
  type DisruptionPnrGroupPage,
  type DisruptionProgress,
  type DisruptionRosterFilters,
  type DispatchCarsResult,
  type DispatchTransfersInput,
  type DispatchTransfersResult,
  type DisruptionTripsPage,
  type IPage,
  type ITransferBoard,
  type MoveSeatsInput,
  type MoveSeatsResult,
  type PlanTransfersInput,
  type RunManifest,
  type ShuttleRunView,
  type TransferRunAction,
  type TransportOutcomeInput,
  type TransportOutcomeResult,
  type CheckinReminderSweepResult,
  type OperatorCheckInInput,
  type OperatorCheckInResult,
  type OperatorLinkResponsibleAdultInput,
  type OperatorLinkResponsibleAdultResult,
  type OperatorSetConditionInput,
  type OperatorSetConditionResult,
  type RebookingDetailView,
  type ResolutionState,
  type ResolveNextResult,
  type ResolutionOp,
  type RosterSummary,
  type IssueGroupVoucherResult,
  type IssueVouchersResult,
} from './types';
import { newIdempotencyKey } from '@/lib/uuid';

/**
 * The JSON body of a failed call. The shared `beforeError` hook (lib/http.ts) has already
 * consumed `error.response` and left the parsed body on `error.body`; re-reading the
 * response (`clone()`) would throw. Falls back to a guarded clone for a client without it.
 */
async function errorBodyOf(error: HTTPError): Promise<Record<string, unknown> | null> {
  const carried = (error as unknown as { body?: unknown }).body;
  if (carried && typeof carried === 'object') return carried as Record<string, unknown>;
  try {
    return (await error.response.clone().json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Re-throw a bulk trigger's 409 as a typed `BulkInProgressError` (S16 / NEX-292).
 * The BFF maps the domain's lock 409 to `{ code:'bulk_in_progress', op,
 * inProgressBy, since }`; we surface it as a typed error the bar catches to show
 * "Already running — started by {holder}" + keep the buttons disabled. A 409 with
 * any other body rethrows unchanged.
 */
async function rethrowBulkConflict(error: unknown): Promise<never> {
  if (error instanceof HTTPError && error.response.status === 409) {
    const body = (await errorBodyOf(error)) as BulkInProgress409 | null;
    if (body?.code === 'bulk_in_progress') {
      throw new BulkInProgressError(body);
    }
  }
  throw error;
}

/**
 * Re-throw a disruption fan-out's 409 `sync_run_in_progress` (another fan-out of the
 * disruption is RUNNING, `sync-run@v1`) as a typed `SyncRunInProgressError`; anything
 * else rethrows unchanged.
 */
async function rethrowSyncConflict(error: unknown): Promise<never> {
  if (error instanceof HTTPError && error.response.status === 409) {
    const body = (await errorBodyOf(error)) as
      | { error?: unknown; runId?: unknown; kind?: unknown; startedAt?: unknown }
      | null;
    if (body?.error === 'sync_run_in_progress') throw new SyncRunInProgressError(body);
  }
  throw error;
}

// Disruptions API client — calls the operator-bff proxy (`/v1/disruptions/*`).
// The BFF owns the `{ items, nextCursor }` envelope + the §6 port mapping; the
// UI passes filters + the opaque cursor through.

/** The network-situation board payload — every open disruption, ranked worst first. */
export async function fetchDisruptionsOverview(): Promise<DisruptionOverview> {
  return http.get('v1/disruptions/overview').json<DisruptionOverview>();
}

export async function fetchDisruptionsPage(
  args: DisruptionFilters & { cursor?: string | undefined },
): Promise<IPage<DisruptionListRow>> {
  const params = new URLSearchParams();
  if (args.cursor) params.set('cursor', args.cursor);
  if (args.status) params.set('status', args.status);
  if (args.cause) params.set('cause', args.cause);
  // Triage view (active = default no-noise, attention = stale-only, past = include resolved).
  if (args.view && args.view !== 'active') params.set('view', args.view);
  // Header airport scope (NEX-310) — the NEX-308 frozen wire; narrowing is
  // server-side (BFF → cases domain, consumed there by S3).
  if (args.airports) params.set('airports', args.airports);
  const qs = params.toString();
  return http.get(`v1/disruptions${qs ? `?${qs}` : ''}`).json<IPage<DisruptionListRow>>();
}

export async function fetchDisruption(urn: string): Promise<DisruptionDetail> {
  return http.get(`v1/disruptions/${encodeURIComponent(urn)}`).json<DisruptionDetail>();
}

/** The BFF's contain refusal (409/502) — the domain's rule said no; carries WHY.
 *  The domain's own `message` is surfaced verbatim (it explains the refusal); the
 *  generic fallback is ours, so it is translated — this module is not a component,
 *  so it resolves the key itself against the console's live language. */
export class ContainRefusedError extends Error {
  readonly code: string;
  readonly facts?: Record<string, number>;
  constructor(body: { error?: string; message?: string; facts?: Record<string, number> }) {
    super(body.message ?? i18next.t('disruptions:contain.refused'));
    this.name = 'ContainRefusedError';
    this.code = body.error ?? 'contain_refused';
    if (body.facts) this.facts = body.facts;
  }
}

/**
 * Contain under the rule (`disruption.tds.md` §3.1 revised): with no `mode` the BFF
 * picks NATURAL when everyone travelled/declined, else FORCED (supervisor; releases
 * holds + selections first; refused while a ticket is issued). A caller that already
 * knows the mode (Operar: resolution-state counts issued-not-departed as settled, the
 * BFF's fallback does not) sends it. A refusal surfaces as `ContainRefusedError` with
 * the domain's message.
 */
export async function containDisruption(
  urn: string,
  note?: string,
  mode?: 'NATURAL' | 'FORCED',
): Promise<DisruptionDetail> {
  try {
    return await http
      .post(`v1/disruptions/${encodeURIComponent(urn)}/contain`, {
        json: { ...(note ? { note } : {}), ...(mode ? { mode } : {}) },
      })
      .json<DisruptionDetail>();
  } catch (error) {
    if (error instanceof HTTPError && (error.response.status === 409 || error.response.status === 502)) {
      const body = (await errorBodyOf(error)) as
        | { error?: string; message?: string; facts?: Record<string, number> }
        | null;
      if (body) throw new ContainRefusedError(body);
    }
    throw error;
  }
}

/** Distinct airports the tenant operates from — drives the airport pickers (declare flow, boards). */
export async function fetchOperatedAirports(): Promise<AirportOption[]> {
  const res = await http.get('v1/disruptions/airports').json<{ items: AirportOption[] }>();
  return res.items ?? [];
}

/* ── S6 (NEX-281) — disruption-wide bulk-command triggers ─────────────────────── */

/** A fresh Idempotency-Key per bulk click — the BFF forwards it so the domain
 * dedupes a double-fire and a re-click correlates to the same runId. */

/* ── disruption-roster-mvp-parity (NEX-375 / U3) — auto-rebook@v2 ──────────────
 * The AUTOMATIC-ACCEPTANCE panel drives the FROZEN operator-bff seam
 * `auto-rebook@v2` (B2). These UI types MIRROR the BFF's `IBulkAcceptPreview` /
 * `IBulkAcceptGroupDecision` (apps/operator-bff/.../auto-rebook.types.ts) VERBATIM
 * — we do NOT import from the BFF (no cross-app compile dep). If the frozen seam
 * moves, update both. */

/** Per-group verdict in a simulated (dryRun) bulk accept. */
export interface BulkAcceptGroupDecision {
  /** Parent case URN (a URN, not a raw locator). */
  caseUrn: string;
  /** PNR group id within the case. */
  groupId: string;
  /** Terminal verdict for this group. */
  decision: 'WOULD_ACCEPT' | 'SKIPPED_BY_CAP' | 'NO_OPTION';
  /** Rank of the chosen option (WOULD_ACCEPT / SKIPPED_BY_CAP). */
  chosenRank?: number;
  /** Total USD PRICE of the chosen option (`airline-money@v2`, priced by the rebooking
   *  domain per component); null = not available. */
  chosenPriceUsd?: number | null;
  /** URN of the chosen option (WOULD_ACCEPT / SKIPPED_BY_CAP). */
  optionUrn?: string;
}

/** The `dryRun` accept preview carried on the 202 of a SIMULATED bulk accept —
 *  nothing was committed. `wouldPriceMinor` is in MINOR units (USD cents), the
 *  airline's PRICE (`airline-money@v2`); null = not available («No disponible»). */
export interface BulkAcceptPreview {
  dryRun: true;
  /** The rank the simulation targeted per group. */
  qualityRank: number;
  /** The PRICE ceiling applied, in whole USD (absent when no cap was set) — P7. */
  priceCapUsd?: number;
  /** Groups that WOULD be accepted. */
  wouldAccept: number;
  /** Groups skipped because the chosen option's price exceeded `priceCapUsd`. */
  skippedByCap: number;
  /** Summed price of the would-accept options, in minor units (USD cents); null = not available. */
  wouldPriceMinor: number | null;
  /** false ⇒ the preview could not be priced and every amount is null («No disponible»). */
  priced?: boolean;
  /** Per-group decisions. */
  decisions: BulkAcceptGroupDecision[];
}

/** v2 result — a `dryRun` request carries `preview` (nothing queued); a real run
 *  omits it (identical to the v1 shape). */
export interface BulkAutoRebookResultV2 extends BulkAutoRebookResult {
  preview?: BulkAcceptPreview;
}

/** The v2 auto-rebook body: v1's `acceptTopRank` plus the AUTOMATIC-ACCEPTANCE
 *  modifiers `dryRun` / `priceCapUsd` / `qualityRank`. */
export interface AutoRebookArgs {
  acceptTopRank?: boolean;
  /** SIMULATE only — book/queue nothing, return `preview` on the 202. */
  dryRun?: boolean;
  /** Per-group PRICE ceiling in whole USD (groups over it → SKIPPED_BY_CAP) — caps mean
   *  the price the airline pays (commercial-contract P7). */
  priceCapUsd?: number;
  /** Target the rank-N option per group instead of rank-1. */
  qualityRank?: 1 | 2 | 3;
  /**
   * Regenerate ("Regenerate options") — force a fresh generation run even though
   * options already exist. The domain preserves committed groups (ticketed /
   * selected) and only recomputes the still-open ones.
   */
  regenerate?: boolean;
}

/**
 * Trigger bulk auto-rebooking across the whole disruption (S6, v2/NEX-375). One
 * BFF call; the domain owns the per-case/per-group fan-out. A `dryRun` request
 * returns the accept `preview` on the 202 (nothing booked); a live request returns
 * `{ queuedCases, queuedGroups, runId }` and the run-state pushes over the WS. The
 * v2 modifiers are forwarded only when set, so a `{ acceptTopRank }` call is the
 * identical v1 wire body as before.
 */
export async function triggerAutoRebook(
  urn: string,
  args: AutoRebookArgs = {},
): Promise<BulkAutoRebookResultV2> {
  const json: Record<string, unknown> = {};
  if (typeof args.acceptTopRank === 'boolean') json.acceptTopRank = args.acceptTopRank;
  if (args.dryRun) json.dryRun = true;
  if (typeof args.priceCapUsd === 'number' && Number.isFinite(args.priceCapUsd)) {
    json.priceCapUsd = args.priceCapUsd;
  }
  if (args.qualityRank === 1 || args.qualityRank === 2 || args.qualityRank === 3) {
    json.qualityRank = args.qualityRank;
  }
  if (args.regenerate) json.regenerate = true;
  try {
    return await http
      .post(`v1/disruptions/${encodeURIComponent(urn)}/auto-rebook`, {
        json,
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      })
      .json<BulkAutoRebookResultV2>();
  } catch (err) {
    // S16: a 409 means another operator's run holds the disruption lock.
    return rethrowBulkConflict(err);
  }
}

/**
 * Trigger hotel allocation for a disruption (S6). One BFF call; the domain pools the
 * cases BY AIRPORT and books. Omit `caseUrns` for the whole event (roster bulk bar);
 * pass a case set for a scoped run. Pass `subCaseUrns` to scope to specific PNRs
 * WITHIN those cases — the PNR drawer's per-PNR "Allocate hotel" sends one of each so
 * only that PNR is booked (a macro case holds many PNRs). Either way the chosen
 * hotels are calculated for the airport, not per case.
 *
 * `idempotencyKey`: a caller that may RETRY the same scope (an error or a timeout after
 * booking already claimed the run) passes the key of the first attempt, so booking
 * replays the run instead of starting another; omitted, every call is a new run.
 */
export async function triggerAllocate(
  urn: string,
  caseUrns?: string[],
  subCaseUrns?: string[],
  idempotencyKey?: string,
): Promise<BulkAllocateResult> {
  try {
    return await http
      .post(`v1/disruptions/${encodeURIComponent(urn)}/allocate`, {
        json: {
          ...(caseUrns && caseUrns.length > 0 ? { caseUrns } : {}),
          ...(subCaseUrns && subCaseUrns.length > 0 ? { subCaseUrns } : {}),
        },
        headers: { 'Idempotency-Key': idempotencyKey ?? newIdempotencyKey() },
      })
      .json<BulkAllocateResult>();
  } catch (err) {
    return rethrowBulkConflict(err);
  }
}

/**
 * Issue meal vouchers across a disruption — the meal counterpart of "Allocate hotels".
 * One BFF call fans out a wallet card-issue per MEAL_VOUCHER group that isn't already
 * issued; wallet emits card-issued → cases flips each roster chip to "Voucher issued".
 * Idempotent on the wallet side, so a re-run only fills the gaps.
 */
export async function triggerIssueVouchers(urn: string): Promise<IssueVouchersResult> {
  return http
    .post(`v1/disruptions/${encodeURIComponent(urn)}/vouchers/issue-all`, {
      json: {},
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
    .json<IssueVouchersResult>()
    .catch(rethrowSyncConflict);
}

/* ── S7 (NEX-282) — control-center roster · progress · cost (frozen S5 BFF) ───── */

/**
 * One page of the disruption-wide roster — grouped by PNR (S12). Server-filtered
 * + cursored; a family is never split across pages (the upstream keysets by
 * sub-case). Filters operate at the group level.
 */
export async function fetchDisruptionRosterPage(
  urn: string,
  args: DisruptionRosterFilters & { cursor?: string | undefined },
): Promise<DisruptionPnrGroupPage> {
  const params = new URLSearchParams();
  if (args.priority) params.set('priority', args.priority);
  if (args.rebookingStatus) params.set('rebookingStatus', args.rebookingStatus);
  if (args.q) params.set('q', args.q);
  if (args.flightUrn) params.set('flightUrn', args.flightUrn);
  if (args.sort && args.sort !== 'default') params.set('sort', args.sort);
  if (args.limit) params.set('limit', String(args.limit));
  if (args.cursor) params.set('cursor', args.cursor);
  const qs = params.toString();
  return http
    .get(`v1/disruptions/${encodeURIComponent(urn)}/passengers${qs ? `?${qs}` : ''}`)
    .json<DisruptionPnrGroupPage>();
}

/** Pollable rebooking-progress rollup for the Progress tab. */
export async function fetchDisruptionProgress(urn: string): Promise<DisruptionProgress> {
  return http
    .get(`v1/disruptions/${encodeURIComponent(urn)}/progress`)
    .json<DisruptionProgress>();
}

/**
 * Per-disruption rebooking DETAIL (NEX-364 / U1) — the one-shot open read the
 * rebooking-progress dialog does to seed the rich per-PNR rows (score components,
 * strategies, original itinerary). Live settle outcomes then arrive over the WS
 * progress feed (`disruption-progress@v2`) — this is NOT polled. The BFF degrades
 * open (empty `groups`) when the rebooking upstream is unwired, so callers render
 * "no results yet" rather than erroring.
 */
export async function fetchRebookingDetail(urn: string): Promise<RebookingDetailView> {
  return http
    .get(`v1/disruptions/${encodeURIComponent(urn)}/rebooking-detail`)
    .json<RebookingDetailView>();
}

/**
 * The 4 MVP header tiles for the disruption Passengers roster
 * (disruption-roster-mvp-parity / U1 — operator-bff:GET
 * /v1/disruptions/{urn}/roster-summary@v1). Read-aggregation over the disruption
 * rollup + rebooking-detail group results; the BFF degrades open (all-zero summary
 * on an upstream miss) so this never rejects for a partial upstream.
 */
export async function fetchRosterSummary(urn: string): Promise<RosterSummary> {
  return http
    .get(`v1/disruptions/${encodeURIComponent(urn)}/roster-summary`)
    .json<RosterSummary>();
}

/** Price headline + per-flight breakdown for the price tab (the route keeps its
 *  `/cost-breakdown` path; every amount is the airline's PRICE — `airline-money@v2`). */
export async function fetchDisruptionPriceBreakdown(
  urn: string,
): Promise<DisruptionPriceBreakdown> {
  return http
    .get(`v1/disruptions/${encodeURIComponent(urn)}/cost-breakdown`)
    .json<DisruptionPriceBreakdown>();
}

/** What the disruption cost — every service, net (`GET /v1/disruptions/:urn/spend@v1`). */
export async function fetchDisruptionSpend(urn: string): Promise<IDisruptionTotalSpend> {
  return http
    .get(`v1/disruptions/${encodeURIComponent(urn)}/spend`)
    .json<IDisruptionTotalSpend>();
}

/* ── disruption-overview (epic NEX-376 / U1) — resolution-state cockpit read ──── */

/**
 * The `resolution-state@v1` read-aggregate (B1) — everything the Overview
 * resolution cockpit needs in ONE read: KPIs + live run progress + the ordered
 * step checklist + problems/implications + the single recommended next step. The
 * BFF fans out to reads it already owns and is fail-soft PER source (a degraded
 * upstream zeros ITS section, never 500s the whole read), so this seeds the cockpit
 * once and the live run-state then arrives over the disruption WS (never a poll).
 */
export async function fetchResolutionState(urn: string): Promise<ResolutionState> {
  return http
    .get(`v1/disruptions/${encodeURIComponent(urn)}/resolution-state`)
    .json<ResolutionState>();
}

/* ── disruption-overview (epic NEX-376 / U3) — resolve-next@v1 one-click trigger ── */

/**
 * Fire the ONE recommended next resolution op (rebooking → hotels → vouchers →
 * contain) via the FROZEN operator-bff `resolve-next@v1` seam (B2). A `dryRun`
 * returns the PREVIEW (what WOULD run + its residual checklist) WITHOUT firing —
 * the cockpit shows the implications confirmation off it, then re-calls with
 * `dryRun:false` to fire it for real. STOPS AT HELD: the seam has no confirm/issue
 * op. Advances one step per call; live per-step progress then arrives over the
 * disruption WS room (the activity panel), never a poll. A held disruption lock
 * comes back as `startedOp:'none'` + an "…already in progress" reason (200) — not an error.
 */
export async function triggerResolveNext(
  urn: string,
  args: { dryRun?: boolean; expectedOp?: ResolutionOp } = {},
): Promise<ResolveNextResult> {
  const json: Record<string, unknown> = {};
  if (args.dryRun) json.dryRun = true;
  // The op the operator previewed/confirmed — if the state moved on, nothing fires.
  if (args.expectedOp) json.expectedOp = args.expectedOp;
  return http
    .post(`v1/disruptions/${encodeURIComponent(urn)}/resolve-next`, { json })
    .json<ResolveNextResult>();
}

/** Disruption-wide "Issue tickets" — every case's assigned flights, idempotent per case. */
export async function issueDisruptionTickets(
  urn: string,
): Promise<{ disruptionUrn: string; tally: Record<string, number> }> {
  return http
    .post(`v1/disruptions/${encodeURIComponent(urn)}/issue-tickets`, {
      json: {},
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
    .json<{ disruptionUrn: string; tally: Record<string, number> }>()
    .catch(rethrowSyncConflict);
}

/** Disruption-wide "Send offers" REPAIR (supervisor, QA fix 4) — re-opens the
 *  acceptance gate for complete offers a missed event left behind ("offers ready,
 *  not sent yet" with no run delivering them). Idempotent per sub-case. */
export async function sendDisruptionOffers(
  urn: string,
): Promise<{ disruptionUrn: string; tally: Record<string, number> }> {
  return http
    .post(`v1/disruptions/${encodeURIComponent(urn)}/send-offers`, {
      json: {},
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
    .json<{ disruptionUrn: string; tally: Record<string, number> }>()
    .catch(rethrowSyncConflict);
}

/**
 * Disruption-wide "Accept all" (supervisor) — every case's pending offers, audited
 * OPERATOR_FORCE. With `subCaseUrns` (`operator-bff:force-accept-all.selective@v1`):
 * only those bookings (non-empty, at most 1000) — the rest are left alone.
 */
export async function forceAcceptAllDisruption(
  urn: string,
  subCaseUrns?: readonly string[],
): Promise<{ disruptionUrn: string; tally: Record<string, number> }> {
  return http
    .post(`v1/disruptions/${encodeURIComponent(urn)}/force-accept-all`, {
      json: subCaseUrns ? { subCaseUrns } : {},
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
    .json<{ disruptionUrn: string; tally: Record<string, number> }>()
    .catch(rethrowSyncConflict);
}

/* ── S8 (NEX-283) — per-PNR-group actions from the roster drawer ─────────────── */

/**
 * Exclude a PNR group (sub-case) from its case — the operator's "this PNR doesn't
 * belong here" action. Proxies the cases-domain `EXCLUDE` transition via the BFF
 * (which resolves the OCC version + injects the acting operator). The caseUrn is
 * passed so the BFF can look up the sub-case's current revision. 202 on accept.
 */
/** Declared accommodation outcome (hotel desk): the PNR is resolved WITHOUT a
 * hotel — SELF_MANAGED (voucher issued, passenger arranges their own stay) or
 * NO_ACCOMMODATION ("we have nothing to offer", formally communicated). Mirrors
 * the exclude endpoint shape; cases stamps operator + timestamp. */
export async function declareAccommodationOutcome(
  disruptionUrn: string,
  subCaseUrn: string,
  body: {
    caseUrn: string;
    outcome: 'SELF_MANAGED' | 'NO_ACCOMMODATION';
    note?: string;
  },
): Promise<{ declared: true; subCaseUrn: string; outcome: string }> {
  return http
    .post(
      `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
        subCaseUrn,
      )}/accommodation-outcome`,
      { json: body },
    )
    .json();
}

export async function excludePnrFromCase(
  disruptionUrn: string,
  subCaseUrn: string,
  caseUrn: string,
): Promise<{ excluded: true; subCaseUrn: string }> {
  return http
    .post(
      `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
        subCaseUrn,
      )}/exclude`,
      { json: { caseUrn } },
    )
    .json<{ excluded: true; subCaseUrn: string }>();
}

/**
 * Issue the meal voucher for ONE PNR group from the roster drawer's MealVoucherCard —
 * the per-PNR counterpart of the disruption-wide "Issue meal vouchers". The BFF issues
 * one wallet card for this sub-case (idempotent upsert keyed `(caseUrn, groupId)`);
 * wallet emits card-issued → cases flips the chip to "Voucher issued". `caseUrn` is
 * passed so the BFF resolves the sub-case's rebooking detail (the roster row doesn't
 * carry it). A fresh Idempotency-Key dedupes a double-click. 200 either way — the body
 * says issued / skipped / wallet status. Routed through `asLockConflict` for symmetry
 * with the other per-PNR writes (no sub-case lock is taken today, but a future one
 * surfaces consistently).
 */
export async function issueGroupVoucher(
  /** `undefined` for a STANDALONE case (no parent disruption) — the issue then
   *  routes through the case-scoped BFF route; same service, same upsert. */
  disruptionUrn: string | undefined,
  subCaseUrn: string,
  caseUrn: string,
  /** DISCRETIONARY issue: the operator chooses the amount and must say why. Omit
   *  to issue the engine's meal-voucher entitlement instead. */
  discretionary?: { amountUsd: number; reason: string },
): Promise<IssueGroupVoucherResult> {
  const path = disruptionUrn
    ? `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
        subCaseUrn,
      )}/vouchers/issue`
    : `v1/cases/${encodeURIComponent(caseUrn)}/pnrs/${encodeURIComponent(
        subCaseUrn,
      )}/vouchers/issue`;
  try {
    return await http
      .post(path, {
        json: { caseUrn, ...(discretionary ?? {}) },
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      })
      .json<IssueGroupVoucherResult>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/**
 * The wallet cards of a STANDALONE case (`GET /v1/cases/:caseUrn/care/cards`).
 * The care board is disruption-scoped — a case outside any disruption reads its
 * issued card(s) here so the drawer can SHOW the money it just handed out.
 * Degrades to `[]` on any error (incl. an older BFF without the route): the
 * panel then renders exactly like an un-issued case.
 */
export async function fetchCaseCareCards(caseUrn: string): Promise<CaseCareCard[]> {
  try {
    return await http
      .get(`v1/cases/${encodeURIComponent(caseUrn)}/care/cards`)
      .json<CaseCareCard[]>();
  } catch {
    return [];
  }
}

/* ── MASS ground transport ("Send to hotels") — the transfer board seam ────────────
 * See the `ITransferBoard` mirror in ./types.ts. Realtime = WS: the board is read
 * ONCE per open and then merged by `TransferRunChanged` / `TransferBoardChanged`
 * (apply-event.ts) — never a poll. */

/**
 * The disruption's transfer board (plan + shuttle runs + per-hotel need). Resolves
 * `null` on a 404 — an older BFF without the route (or a disruption with no
 * transport) — so the Tower can draw the Transfers gate as "not wired" instead of
 * erroring the Hotels view.
 */
export async function fetchTransferBoard(urn: string): Promise<ITransferBoard | null> {
  try {
    return await http
      .get(`v1/disruptions/${encodeURIComponent(urn)}/transfers`)
      .json<ITransferBoard>();
  } catch (err) {
    if (err instanceof HTTPError && err.response.status === 404) return null;
    throw err;
  }
}

/** Enrol the missing passengers + (re-)cut the PLANNED runs (per hotel or the whole plan). */
export async function planTransfers(
  urn: string,
  input: PlanTransfersInput = {},
): Promise<ITransferBoard> {
  return http
    .post(`v1/disruptions/${encodeURIComponent(urn)}/transfers/plan`, {
      json: input,
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
    .json<ITransferBoard>();
}

/** Dispatch the first wave (or the named runs / hotel). 202 — echoes already-dispatched runs. */
export async function dispatchTransfers(
  urn: string,
  input: DispatchTransfersInput,
): Promise<DispatchTransfersResult> {
  return http
    .post(`v1/disruptions/${encodeURIComponent(urn)}/transfers/dispatch`, {
      json: input,
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
    .json<DispatchTransfersResult>();
}

/**
 * One operator action on ONE run: cancel (409 once departed), board / no-show /
 * own-means (per passenger — `passengerUrn` in the body), replace-no-shows (a new
 * run for the NO_SHOW seats). Returns the run (or `{run}`) — the WS echo lands the
 * same state, so a duplicate delivery is harmless.
 */
export async function transferRunAction(
  runUrn: string,
  action: TransferRunAction,
  body: { reason?: string; passengerUrn?: string } = {},
): Promise<{ run?: ShuttleRunView } & Record<string, unknown>> {
  return http
    .post(`v1/transport/runs/${encodeURIComponent(runUrn)}/${action}`, {
      json: body,
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
    .json<{ run?: ShuttleRunView } & Record<string, unknown>>();
}

/* ── feat/care-desk K5 (NEX-457) — care board read + care write proxies ──────────
 * Realtime = WS: the board is read ONCE per open and then merged by `CareChanged`
 * (apply-event.ts) — never a poll. Writes carry a fresh Idempotency-Key so a
 * network retry can't double-fund / double-declare. */

/** The disruption's care board (per entitled PNR: card + balances + facts).
 *  Degrade-open on the BFF (never a 500) — `known` carries the honesty flags. */
export async function fetchCareBoard(urn: string): Promise<CareBoard> {
  return http.get(`v1/disruptions/${encodeURIComponent(urn)}/care`).json<CareBoard>();
}

/**
 * The PNR card's history (care-card history, 2026-10-30): every load and adjustment —
 * when, how much, who, why. `null` on a 404 (an older BFF without the route); the BFF
 * itself degrades open (`known: false`) when the wallet does not answer.
 */
export function fetchCareHistory(disruptionUrn: string, subCaseUrn: string, caseUrn: string): Promise<CareCardHistory | null> {
  return careRead(`${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(subCaseUrn)}/care/history?caseUrn=${encodeURIComponent(caseUrn)}`);
}

/** A care read under `v1/disruptions/…`; `null` on a 404 (an older BFF without the route). */
async function careRead<T>(path: string): Promise<T | null> {
  try {
    return await http.get(`v1/disruptions/${path}`).json<T>();
  } catch (err) {
    if (err instanceof HTTPError && err.response.status === 404) return null;
    throw err;
  }
}

/** What the disruption gave its passengers (totals, batches, per PNR). `null` on a 404 (older BFF). */
export function fetchCareMoney(disruptionUrn: string): Promise<CareMoney | null> {
  return careRead(`${encodeURIComponent(disruptionUrn)}/care/money`);
}

/** The per-PNR money list as a CSV blob plus the filename the BFF stamped. */
export async function fetchCareMoneyCsv(disruptionUrn: string): Promise<{ blob: Blob; filename: string }> {
  const res = await http.get(`v1/disruptions/${encodeURIComponent(disruptionUrn)}/care/money.csv`);
  const match = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '');
  return { blob: await res.blob(), filename: match?.[1] ?? 'dinero.csv' };
}

/** The disruption's card loads in batches (each meal tier, the allowance, each grant). `null` on a 404. */
export function fetchCareLoads(disruptionUrn: string): Promise<CareLoads | null> {
  return careRead(`${encodeURIComponent(disruptionUrn)}/care/loads`);
}

/**
 * Discretionary top-up of the group's live card (wallet proxy, 202 — the money
 * settles asynchronously; `CareChanged` flips the "settling" chip). K5 lands the
 * seam; the K6 top-up dialog drives it. Routed through `asLockConflict` so a
 * held record lock surfaces as a typed conflict naming the holder.
 *
 * `idempotencyKey`: a caller that may RETRY the same grant (a timeout or a 5xx after
 * the wallet already loaded the card) passes the key of the first attempt, so the
 * wallet dedupes instead of crediting twice; omitted, every call is a new grant.
 */
export async function postCareTopUp(
  disruptionUrn: string,
  subCaseUrn: string,
  body: CareTopUpInput,
  idempotencyKey?: string,
): Promise<CareTopUpResult> {
  try {
    return await http
      .post(
        `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
          subCaseUrn,
        )}/care/top-up`,
        { json: body, headers: { 'Idempotency-Key': idempotencyKey ?? newIdempotencyKey() } },
      )
      .json<CareTopUpResult>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/* ── transport-desk (slice T3, NEX-463) — trips read + dispatch-cars ─────────────
 * Realtime = WS: the trips page is read ONCE per open and then upserted by the
 * existing `TripChanged` push (apply-event.ts) — never a poll. */

/**
 * The disruption's CAR / on-demand trips. Resolves `null` on a 404 — an older
 * BFF without the route — so the Transport tab draws the cars strip as "not
 * wired" instead of erroring; the BFF itself degrades open (`trips: []` +
 * `known.transport:false`) when the transport domain is unreachable.
 */
export async function fetchDisruptionTrips(urn: string): Promise<DisruptionTripsPage | null> {
  try {
    return await http
      .get(`v1/disruptions/${encodeURIComponent(urn)}/trips`)
      .json<DisruptionTripsPage>();
  } catch (err) {
    if (err instanceof HTTPError && err.response.status === 404) return null;
    throw err;
  }
}

/**
 * Declare a terminal counter outcome for one PNR's care story (cases proxy —
 * COUNTER_VOUCHER / HANDLED_OUTSIDE). The BFF injects the VERIFIED operator as
 * the actor; cases stamps and never moves money. 202.
 */
export async function postCareOutcome(
  disruptionUrn: string,
  subCaseUrn: string,
  body: { caseUrn: string; outcome: CareOutcomeKind; note?: string },
): Promise<{ declared: true; subCaseUrn: string; outcome: string }> {
  try {
    return await http
      .post(
        `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
          subCaseUrn,
        )}/care-outcome`,
        { json: body, headers: { 'Idempotency-Key': newIdempotencyKey() } },
      )
      .json<{ declared: true; subCaseUrn: string; outcome: string }>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/**
 * The below-threshold mass CAR fan-out: one domain dispatch per roster group
 * still needing ground transport. Mandatory operator `reason` (audit trail);
 * already-served groups are skipped domain-side, so a re-click is harmless.
 */
export async function triggerDispatchCars(urn: string, reason: string): Promise<DispatchCarsResult> {
  return http
    .post(`v1/disruptions/${encodeURIComponent(urn)}/transport/dispatch-cars`, {
      json: { reason },
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
    .json<DispatchCarsResult>();
}

/* ── transport-desk (slice T4, NEX-464) — run manifest + move-seats + add-run ──── */

/**
 * Rethrow a transport-desk write's coded refusal (`{error: 'run_departed' |
 * 'over_capacity' | 'plan_not_found' | …}`) as a typed `TransportSeamError` the
 * dialogs branch on synchronously. Any other failure rethrows unchanged.
 */
async function rethrowTransportSeam(error: unknown): Promise<never> {
  if (error instanceof HTTPError) {
    const body = (await errorBodyOf(error)) as {
      error?: string;
    } | null;
    if (typeof body?.error === 'string') {
      throw new TransportSeamError(error.response.status, body.error, body.error);
    }
  }
  throw error;
}

/**
 * The run's manifest (`SeatView[]`) — URNs ONLY, never a name. The run sheet
 * joins the roster walk client-side so passenger identity stays mask-by-default.
 * Seeded once per open; re-read on the run's own `TransferRunChanged` push.
 */
export async function fetchRunManifest(runUrn: string): Promise<RunManifest> {
  return http
    .get(`v1/transport/runs/${encodeURIComponent(runUrn)}/manifest`)
    .json<RunManifest>();
}

/**
 * Move the named passengers off `runUrn` onto `toRunUrn` (family-grain: the UI
 * always sends whole PNR clusters). The domain's 409s — `run_departed` (the
 * target left) and `over_capacity` (someone else took the seats) — surface as
 * `TransportSeamError` so the dialog stays open with the stale-state copy.
 */
export async function postMoveSeats(
  runUrn: string,
  input: MoveSeatsInput,
): Promise<MoveSeatsResult> {
  try {
    return await http
      .post(`v1/transport/runs/${encodeURIComponent(runUrn)}/move-seats`, {
        json: input,
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      })
      .json<MoveSeatsResult>();
  } catch (err) {
    return rethrowTransportSeam(err);
  }
}

/**
 * Void the group's prepaid card (wallet proxy — OPS_SUPERVISOR only, reason
 * mandatory; K6). Irreversible for the group: a voided card can never be
 * re-issued — the counter-voucher outcome is the only remaining path.
 */
/**
 * Mark the group's benefit CONSUMED after the wallet validates the PRINTED
 * coupon reference (#808). Wallet verdicts pass through: 422 `reference_mismatch`,
 * 409 `already_redeemed` (carrying the existing record) / `card_voided`.
 */
export async function postCareRedeem(
  disruptionUrn: string,
  subCaseUrn: string,
  body: { caseUrn?: string; reference: string; amount?: number; note?: string },
): Promise<CareRedeemResult> {
  try {
    return await http
      .post(
        `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
          subCaseUrn,
        )}/care/redeem`,
        { json: body, headers: { 'Idempotency-Key': newIdempotencyKey() } },
      )
      .json<CareRedeemResult>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/** The card rows of the disruption whose printed coupon reference matches (#808). */
export async function fetchCareLookup(disruptionUrn: string, reference: string): Promise<CareLookupResult> {
  return http
    .get(`v1/disruptions/${encodeURIComponent(disruptionUrn)}/care/lookup`, {
      searchParams: { reference },
    })
    .json<CareLookupResult>();
}

export async function postCareVoid(
  disruptionUrn: string,
  subCaseUrn: string,
  body: { caseUrn?: string; reason: string },
): Promise<{ voided: true; subCaseUrn: string }> {
  try {
    return await http
      .post(
        `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
          subCaseUrn,
        )}/care/void`,
        { json: body, headers: { 'Idempotency-Key': newIdempotencyKey() } },
      )
      .json<{ voided: true; subCaseUrn: string }>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/**
 * Add one extra run for a hotel + leg (the operator tops up the wave by hand);
 * `dispatchNow` sends it straight to the pickup. 404 `plan_not_found` (no plan
 * cut yet) surfaces as `TransportSeamError` — the dialog says "Plan shuttles
 * first" instead of erroring.
 */
export async function postAddRun(
  disruptionUrn: string,
  input: AddRunInput,
): Promise<AddRunResult> {
  try {
    return await http
      .post(`v1/disruptions/${encodeURIComponent(disruptionUrn)}/transfers/add-run`, {
        json: input,
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      })
      .json<AddRunResult>();
  } catch (err) {
    return rethrowTransportSeam(err);
  }
}

/**
 * Declare the SEATLESS own-means outcome for one PNR (T2b seam): the group covers
 * the ground leg themselves — no seat to release, no trip to cancel. The BFF
 * proxies the cases denorm write with the VERIFIED operator as the actor; the
 * roster row's `transportOutcome` ends the PNR's transport story in the desk
 * classifier. Coded refusals (400 `invalid_outcome`, 404) rethrow typed.
 */
export async function postTransportOutcome(
  disruptionUrn: string,
  subCaseUrn: string,
  input: TransportOutcomeInput,
): Promise<TransportOutcomeResult> {
  try {
    return await http
      .post(
        `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
          subCaseUrn,
        )}/transport-outcome`,
        { json: input, headers: { 'Idempotency-Key': newIdempotencyKey() } },
      )
      .json<TransportOutcomeResult>();
  } catch (err) {
    return rethrowTransportSeam(err);
  }
}

/* ── transport-desk (slice T7, NEX-467) — the check-in loop ─────────────────────── */

/**
 * Record ONE passenger as checked in at the hotel — the operator fallback of the
 * SMS/WhatsApp loop (T2b proxy of the cases K3 seam). The BFF stamps
 * `source:'OPERATOR'` + the VERIFIED operator as the actor; the client never names
 * either. A fresh Idempotency-Key dedupes a double-click. Routed through
 * `asLockConflict` for symmetry with the other per-PNR writes.
 */
export async function postOperatorCheckIn(
  disruptionUrn: string,
  subCaseUrn: string,
  paxUrn: string,
  input: OperatorCheckInInput,
): Promise<OperatorCheckInResult> {
  try {
    return await http
      .post(
        `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
          subCaseUrn,
        )}/passengers/${encodeURIComponent(paxUrn)}/check-in`,
        { json: input, headers: { 'Idempotency-Key': newIdempotencyKey() } },
      )
      .json<OperatorCheckInResult>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/* ── #806 — accommodation condition (minors rule) ──────────────────────────────── */

/**
 * Set ONE passenger's adult / minor condition from the desk (#806). The BFF stamps
 * the VERIFIED operator as the actor. Overwritable; a fresh Idempotency-Key
 * dedupes a double-click. Routed through `asLockConflict` like the other per-PNR
 * writes.
 */
export async function postPassengerCondition(
  disruptionUrn: string,
  subCaseUrn: string,
  paxUrn: string,
  input: OperatorSetConditionInput,
): Promise<OperatorSetConditionResult> {
  try {
    return await http
      .post(
        `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
          subCaseUrn,
        )}/passengers/${encodeURIComponent(paxUrn)}/condition`,
        { json: input, headers: { 'Idempotency-Key': newIdempotencyKey() } },
      )
      .json<OperatorSetConditionResult>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/**
 * Link a minor to their responsible adult on ANOTHER PNR of the same case (#806,
 * test case 8b). Resolves an `UNKNOWN` into `ACCOMPANIED_MINOR` server-side.
 */
export async function postResponsibleAdult(
  disruptionUrn: string,
  subCaseUrn: string,
  paxUrn: string,
  input: OperatorLinkResponsibleAdultInput,
): Promise<OperatorLinkResponsibleAdultResult> {
  try {
    return await http
      .post(
        `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(
          subCaseUrn,
        )}/passengers/${encodeURIComponent(paxUrn)}/responsible-adult`,
        { json: input, headers: { 'Idempotency-Key': newIdempotencyKey() } },
      )
      .json<OperatorLinkResponsibleAdultResult>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/**
 * Run the check-in reminder sweep NOW (T2b proxy of the notifications reconcile):
 * claims every reminder-due arrival and mints the reminder sends. The worker runs
 * the same sweep on its own clock — this button just refuses to wait for it.
 * Idempotent domain-side (claimed rows are claimed atomically); re-clicks are
 * harmless. Routed through `asLockConflict` for symmetry with the other writes.
 */
export async function triggerCheckinSweep(
  disruptionUrn: string,
): Promise<CheckinReminderSweepResult> {
  try {
    return await http
      .post(
        `v1/disruptions/${encodeURIComponent(disruptionUrn)}/transport/checkin-reminders/sweep`,
        { json: {}, headers: { 'Idempotency-Key': newIdempotencyKey() } },
      )
      .json<CheckinReminderSweepResult>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/* ── #819 (SKY PoC A9) — event-level benefit override ─────────────────────────── */

export async function fetchBenefitOverrides(disruptionUrn: string): Promise<BenefitOverride[]> {
  return http
    .get(`v1/disruptions/${encodeURIComponent(disruptionUrn)}/benefit-overrides`)
    .json<BenefitOverride[]>();
}

export async function postBenefitOverride(
  disruptionUrn: string,
  body: ProposeBenefitOverrideInput,
): Promise<BenefitOverride> {
  return http
    .post(`v1/disruptions/${encodeURIComponent(disruptionUrn)}/benefit-overrides`, { json: body })
    .json<BenefitOverride>();
}

export async function postBenefitOverrideDecision(
  disruptionUrn: string,
  overrideUrn: string,
  body: { decision: 'APPROVE' | 'REJECT'; note?: string },
): Promise<BenefitOverride> {
  return http
    .post(
      `v1/disruptions/${encodeURIComponent(disruptionUrn)}/benefit-overrides/${encodeURIComponent(overrideUrn)}/decide`,
      { json: body },
    )
    .json<BenefitOverride>();
}

/** One call for the whole event — idempotent on the BFF/cases side (a second
 *  apply returns the stored application without re-issuing). */
export async function postBenefitOverrideApply(
  disruptionUrn: string,
  overrideUrn: string,
): Promise<ApplyBenefitOverrideResult> {
  return http
    .post(
      `v1/disruptions/${encodeURIComponent(disruptionUrn)}/benefit-overrides/${encodeURIComponent(overrideUrn)}/apply`,
      { json: {}, timeout: 60_000 },
    )
    .json<ApplyBenefitOverrideResult>();
}
